import { z } from "zod";

/**
 * Image masks ("Bildformen"): the outline an image is clipped to.
 *
 * Every mask is SVG path data in a normalised 0..1 box (absolute M/L/C/Q/A/Z
 * commands only), scaled to the element's box when rendered. `square` masks
 * keep their proportions (a circle stays round) and sit centred in the box.
 * `polygon` and `star` are generated like the shapes of the same name, so an
 * image dropped onto such a placeholder gets exactly its outline.
 */
export const MASK_SHAPES = [
  "circle",
  "ellipse",
  "triangle",
  "polygon",
  "star",
  "heart",
  "arch",
  "drop",
  "leaf",
  "speech",
  "blob1",
  "blob2",
  "blob3",
  "wave",
  "arrow",
] as const;
export type MaskShape = (typeof MASK_SHAPES)[number];

export const imageMaskSchema = z.object({
  shape: z.enum(MASK_SHAPES),
  /** Corners of `polygon` / points of `star`. */
  sides: z.number().int().min(3).max(24).optional(),
  /** Inner radius of `star` relative to the outer radius. */
  innerRatio: z.number().min(0.1).max(0.95).optional(),
});
export type ImageMask = z.infer<typeof imageMaskSchema>;

interface MaskDefinition {
  /** Normalised path data (0..1). */
  path: string;
  /** Keep the aspect ratio (centred square of the shorter side). */
  square?: boolean;
}

const ELLIPSE = "M0,0.5 A0.5,0.5 0 1 1 1,0.5 A0.5,0.5 0 1 1 0,0.5 Z";

const MASK_PATHS: Record<Exclude<MaskShape, "polygon" | "star" | "arch">, MaskDefinition> = {
  circle: { path: ELLIPSE, square: true },
  ellipse: { path: ELLIPSE },
  triangle: { path: "M0.5,0 L1,1 L0,1 Z" },
  heart: {
    path: "M0.5,1 C0.5,1 0,0.66 0,0.3 C0,0.12 0.13,0 0.28,0 C0.38,0 0.46,0.06 0.5,0.14 C0.54,0.06 0.62,0 0.72,0 C0.87,0 1,0.12 1,0.3 C1,0.66 0.5,1 0.5,1 Z",
    square: true,
  },
  drop: { path: "M0.5,0 L1,0 L1,0.5 A0.5,0.5 0 1 1 0.5,0 Z", square: true },
  leaf: {
    path: "M0,1 L0,0.5 A0.5,0.5 0 0 1 0.5,0 L1,0 L1,0.5 A0.5,0.5 0 0 1 0.5,1 Z",
    square: true,
  },
  speech: {
    path: "M0.12,0 L0.88,0 Q1,0 1,0.12 L1,0.66 Q1,0.78 0.88,0.78 L0.42,0.78 L0.2,1 L0.26,0.78 L0.12,0.78 Q0,0.78 0,0.66 L0,0.12 Q0,0 0.12,0 Z",
  },
  blob1: {
    path: "M0.52,0.02 C0.74,0 0.95,0.14 0.98,0.4 C1,0.62 0.9,0.86 0.66,0.96 C0.44,1 0.16,0.94 0.06,0.72 C0,0.52 0.02,0.28 0.18,0.14 C0.28,0.06 0.4,0.03 0.52,0.02 Z",
  },
  blob2: {
    path: "M0.3,0.04 C0.46,0 0.62,0.08 0.76,0.06 C0.92,0.04 1,0.2 0.98,0.38 C0.96,0.54 0.86,0.6 0.9,0.76 C0.94,0.92 0.78,1 0.6,0.98 C0.44,0.96 0.34,0.9 0.2,0.92 C0.06,0.94 0,0.8 0.02,0.64 C0.04,0.5 0.12,0.42 0.08,0.28 C0.04,0.12 0.14,0.08 0.3,0.04 Z",
  },
  blob3: {
    path: "M0.5,0 C0.68,0 0.78,0.12 0.88,0.22 C0.98,0.32 1,0.44 0.96,0.58 C0.92,0.74 0.98,0.9 0.82,0.97 C0.66,1 0.54,0.9 0.4,0.94 C0.24,0.98 0.06,0.92 0.02,0.74 C0,0.6 0.1,0.5 0.06,0.36 C0.02,0.2 0.1,0.06 0.26,0.02 C0.36,0 0.42,0 0.5,0 Z",
  },
  wave: { path: "M0,0 L1,0 L1,0.86 C0.84,0.8 0.7,0.8 0.5,0.9 C0.3,1 0.16,1 0,0.92 Z" },
  arrow: { path: "M0,0.25 L0.6,0.25 L0.6,0 L1,0.5 L0.6,1 L0.6,0.75 L0,0.75 Z" },
};

/** Number of numeric arguments per command. */
const ARITY: Record<string, number> = { M: 2, L: 2, C: 6, Q: 4, A: 7, Z: 0 };

function round(value: number): string {
  const rounded = Math.round(value * 100) / 100;
  return Object.is(rounded, -0) ? "0" : String(rounded);
}

/**
 * Scales normalised path data (absolute M/L/C/Q/A/Z) to a box of `width` ×
 * `height` at (`dx`, `dy`). Arcs must not be rotated (x-axis-rotation 0), so
 * scaling their radii keeps them exact.
 */
export function scalePath(d: string, width: number, height: number, dx = 0, dy = 0): string {
  const tokens = d.match(/[a-zA-Z]|-?\d*\.?\d+(?:e-?\d+)?/g) ?? [];
  const out: string[] = [];
  let i = 0;
  while (i < tokens.length) {
    const command = tokens[i++]!;
    const arity = ARITY[command];
    if (arity === undefined) throw new Error(`Unsupported path command: ${command}`);
    if (arity === 0) {
      out.push(command);
      continue;
    }
    // Implicit repetition ("L1,0 1,1") is allowed.
    do {
      const args = tokens.slice(i, i + arity).map(Number);
      if (args.length < arity || args.some((n) => Number.isNaN(n))) {
        throw new Error(`Invalid path arguments after ${command}`);
      }
      i += arity;
      if (command === "A") {
        const [rx, ry, rotation, large, sweep, x, y] = args as [
          number,
          number,
          number,
          number,
          number,
          number,
          number,
        ];
        out.push(
          `A${round(rx * width)},${round(ry * height)} ${rotation} ${large} ${sweep} ` +
            `${round(dx + x * width)},${round(dy + y * height)}`,
        );
        continue;
      }
      const scaled = args.map((n, index) =>
        round(index % 2 === 0 ? dx + n * width : dy + n * height),
      );
      out.push(`${command}${scaled.join(",")}`);
    } while (i < tokens.length && !/[a-zA-Z]/.test(tokens[i]!));
  }
  return out.join(" ");
}

function pointsPath(points: [number, number][]): string {
  return `${points.map(([x, y], index) => `${index === 0 ? "M" : "L"}${round(x)},${round(y)}`).join(" ")} Z`;
}

/** Regular polygon / star in a box, first corner at the top (like the shapes). */
function radialPath(width: number, height: number, corners: number, inner: number | null) {
  const count = inner === null ? corners : corners * 2;
  const points: [number, number][] = [];
  for (let i = 0; i < count; i++) {
    const angle = -Math.PI / 2 + (i * 2 * Math.PI) / count;
    const factor = inner !== null && i % 2 === 1 ? inner : 1;
    points.push([
      width / 2 + (width / 2) * factor * Math.cos(angle),
      height / 2 + (height / 2) * factor * Math.sin(angle),
    ]);
  }
  return pointsPath(points);
}

/** Outline of a mask as SVG path data in a box of `width` × `height` (px). */
export function maskPath(mask: ImageMask, width: number, height: number): string {
  const w = Math.max(0, width);
  const h = Math.max(0, height);
  switch (mask.shape) {
    case "polygon":
      return radialPath(w, h, mask.sides ?? 6, null);
    case "star":
      return radialPath(w, h, mask.sides ?? 5, mask.innerRatio ?? 0.5);
    case "arch": {
      // Semicircle on top, as wide as the box (flatter when the box is low).
      const r = Math.min(w / 2, h);
      return `M0,${round(h)} L0,${round(r)} A${round(w / 2)},${round(r)} 0 0 1 ${round(w)},${round(r)} L${round(w)},${round(h)} Z`;
    }
    default: {
      const definition = MASK_PATHS[mask.shape];
      if (!definition.square) return scalePath(definition.path, w, h);
      const side = Math.min(w, h);
      return scalePath(definition.path, side, side, (w - side) / 2, (h - side) / 2);
    }
  }
}

/** CSS `clip-path` value of a mask for a box of `width` × `height` (px). */
export function maskClipPath(mask: ImageMask, width: number, height: number): string {
  return `path("${maskPath(mask, width, height)}")`;
}

/**
 * The mask matching the outline of an image placeholder shape, so an image
 * dropped onto an ellipse / triangle / polygon / star keeps exactly that form.
 * Rectangles return `undefined` (they use the corner radius).
 */
export function maskForShape(shape: {
  shape: string;
  sides: number;
  innerRatio: number;
}): ImageMask | undefined {
  switch (shape.shape) {
    case "ellipse":
      return { shape: "ellipse" };
    case "triangle":
      return { shape: "triangle" };
    case "polygon":
      return { shape: "polygon", sides: shape.sides };
    case "star":
      return { shape: "star", sides: shape.sides, innerRatio: shape.innerRatio };
    default:
      return undefined;
  }
}

export interface MaskPreset {
  id: string;
  label: string;
  mask: ImageMask;
}

/** The forms of the "Form" picker (images) and the frames of the Elemente panel. */
export const MASK_PRESETS: MaskPreset[] = [
  { id: "circle", label: "Kreis", mask: { shape: "circle" } },
  { id: "ellipse", label: "Oval", mask: { shape: "ellipse" } },
  { id: "arch", label: "Bogen", mask: { shape: "arch" } },
  { id: "triangle", label: "Dreieck", mask: { shape: "triangle" } },
  { id: "diamond", label: "Raute", mask: { shape: "polygon", sides: 4 } },
  { id: "pentagon", label: "Fünfeck", mask: { shape: "polygon", sides: 5 } },
  { id: "hexagon", label: "Sechseck", mask: { shape: "polygon", sides: 6 } },
  { id: "star", label: "Stern", mask: { shape: "star", sides: 5, innerRatio: 0.5 } },
  { id: "burst", label: "Siegel", mask: { shape: "star", sides: 16, innerRatio: 0.85 } },
  { id: "heart", label: "Herz", mask: { shape: "heart" } },
  { id: "drop", label: "Tropfen", mask: { shape: "drop" } },
  { id: "leaf", label: "Blatt", mask: { shape: "leaf" } },
  { id: "speech", label: "Sprechblase", mask: { shape: "speech" } },
  { id: "blob1", label: "Blob 1", mask: { shape: "blob1" } },
  { id: "blob2", label: "Blob 2", mask: { shape: "blob2" } },
  { id: "blob3", label: "Blob 3", mask: { shape: "blob3" } },
  { id: "wave", label: "Welle unten", mask: { shape: "wave" } },
  { id: "arrow", label: "Pfeil", mask: { shape: "arrow" } },
];

/** Same outline (shape and parameters, defaults filled in). */
export function sameMask(a: ImageMask | undefined, b: ImageMask | undefined): boolean {
  if (!a || !b) return !a && !b;
  if (a.shape !== b.shape) return false;
  if (a.shape === "polygon") return (a.sides ?? 6) === (b.sides ?? 6);
  if (a.shape === "star") {
    return (a.sides ?? 5) === (b.sides ?? 5) && (a.innerRatio ?? 0.5) === (b.innerRatio ?? 0.5);
  }
  return true;
}
